import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment-timezone';
import { ItemOfertaData, CotizacionData } from '../../../types';
import { validarCantidadContraPedido, validarFecha } from '../../../functions';

interface DeliveryFieldsProps {
  currentItem: ItemOfertaData;
  itemIndex: number;
  parentData: CotizacionData;
  maxUnidadesSolicitadas: number;
  localSelectedDate: string;
  setLocalSelectedDate: (date: string) => void;
  handleItemChange: (field: keyof ItemOfertaData, value: string | number) => void;
  shouldValidate: boolean;
}

export const DeliveryFields: React.FC<DeliveryFieldsProps> = ({
  currentItem,
  itemIndex,
  parentData,
  maxUnidadesSolicitadas,
  localSelectedDate,
  setLocalSelectedDate,
  handleItemChange,
  shouldValidate,
}) => {
  const getValidoHastaHelperText = () => {
    if (!shouldValidate) return '';
    const error = validarFecha(localSelectedDate, parentData);
    if (error) return error;
    return !localSelectedDate ? 'Campo requerido' : '';
  };

  const getFechaEntregaHelperText = () => {
    if (!shouldValidate) return '';
    const error = validarFecha(currentItem.fecha_entrega, parentData);
    if (error) return error;
    return !currentItem.fecha_entrega ? 'Campo requerido' : '';
  };

  const getUnidadesHelperText = () => {
    if (!shouldValidate) return '';
    const errorCantidad = validarCantidadContraPedido(
      currentItem.cantidad,
      maxUnidadesSolicitadas,
    );
    if (errorCantidad) return errorCantidad;
    if (!currentItem.cantidad || currentItem.cantidad <= 0) {
      return 'Campo requerido';
    }
    return '';
  };

  const hasUnidadesError =
    shouldValidate &&
    (!currentItem.cantidad ||
      currentItem.cantidad <= 0 ||
      !!validarCantidadContraPedido(currentItem.cantidad, maxUnidadesSolicitadas));

  return (
    <>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          id={`unidadesDisponibles-${itemIndex}`}
          label="Und. Disp."
          type="number"
          fullWidth
          value={currentItem.cantidad}
          onChange={(e) =>
            handleItemChange('cantidad', Number(e.target.value) || 0)
          }
          required={shouldValidate}
          slotProps={{
            htmlInput: {
              min: 1,
              max: maxUnidadesSolicitadas,
            },
          }}
          error={hasUnidadesError}
          helperText={getUnidadesHelperText()}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormControl fullWidth sx={{ marginRight: 2 }} data-testid="tipo-entrega-form-control" required={shouldValidate} error={shouldValidate && !currentItem.tipo_entrega}>
          <InputLabel id={`deliveryType-${itemIndex}-label`}>Tipo Entrega</InputLabel>
          <Select
            labelId={`deliveryType-${itemIndex}-label`}
            id={`deliveryType-${itemIndex}`}
            label="Tipo Entrega"
            value={currentItem.tipo_entrega}
            onChange={(e) =>
              handleItemChange('tipo_entrega', e.target.value)
            }
            required={shouldValidate}
          >
            <MenuItem value="op1">Inmediata</MenuItem>
            <MenuItem value="op2">Plazo</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <DatePicker
          label="Válido Hasta"
          value={localSelectedDate ? moment(localSelectedDate) : null}
          onChange={(newValue) => {
            const dateString = newValue
              ? newValue.format('YYYY-MM-DD')
              : '';
            setLocalSelectedDate(dateString);
            // Actualizar solo el campo valido_hasta, no observaciones
            handleItemChange('valido_hasta', dateString);
          }}
          format="YYYY-MM-DD"
          slotProps={{
            textField: {
              id: `validoHasta-${itemIndex}`,
              fullWidth: true,
              required: shouldValidate,
              error: shouldValidate && (!!validarFecha(localSelectedDate, parentData) || !localSelectedDate),
              helperText: getValidoHastaHelperText(),
            },
          }}
        />
      </Grid>
      {currentItem.tipo_entrega === 'op2' && (
        <Grid size={{ xs: 12, md: 4 }}>
          <DatePicker
            label="Fecha Entrega"
            value={
              currentItem.fecha_entrega
                ? moment(currentItem.fecha_entrega)
                : null
            }
            onChange={(newValue) => {
              const dateString = newValue
                ? newValue.format('YYYY-MM-DD')
                : '';
              handleItemChange('fecha_entrega', dateString);
            }}
            format="YYYY-MM-DD"
            slotProps={{
              textField: {
                id: `deliveryDate-${itemIndex}`,
                fullWidth: true,
                required: shouldValidate,
                error: shouldValidate && (
                  !!validarFecha(currentItem.fecha_entrega, parentData) ||
                  !currentItem.fecha_entrega
                ),
                helperText: getFechaEntregaHelperText(),
              },
            }}
          />
        </Grid>
      )}
    </>
  );
};

